import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Field, reduxForm, formValueSelector } from 'redux-form';
import { connect } from 'react-redux';
import { Button, Col, Form, FormGroup, Input, Label, Row } from 'reactstrap';
import { toastr } from 'react-redux-toastr';
import Loader from '../../../common/Loader';
import FileDetailResource from '../../../common/resource/FileDetailResource';
import FormFooter from '../../../common/forms/FormFooter';
import { getPricingProfileMassiveUpdateTemplate } from '../../../../actions';
import DatePicker from '../../../common/forms/input/DatePicker';
import { isRequired } from '../../../../utils/validators';
import TextInput from '../../../common/forms/input/TextInput';
import FormItem from '../../../common/forms/FormItem';

const PricingProfileMassiveUpdateForm = ({
  handleSubmit,
  loading,
  onSubmit,
  dispatchGetPricingProfileMassiveUpdateTemplate,
  corporate,
  startDate,
  endDate,
}) => {
  const [selectedFile, setSelectedFile] = useState(null);

  const isValidFileType = (file) => {
    const validExtensions = ['.xlsx'];
    const fileName = file.name.toLowerCase();
    return validExtensions.some((ext) => fileName.endsWith(ext));
  };

  const normalizeDate = (date) => {
    if (!date) return null;

    if (date.format) {
      return date.format('YYYY-MM-DD');
    }

    if (date instanceof Date && !Number.isNaN(date.getTime())) {
      const year = date.getFullYear();
      const month = `${date.getMonth() + 1}`.padStart(2, '0');
      const day = `${date.getDate()}`.padStart(2, '0');
      return `${year}-${month}-${day}`;
    }

    return date;
  };

  const onChangeVoucher = (e) => {
    const file = e.target.files[0];

    if (file && isValidFileType(file)) {
      setSelectedFile(file);
    } else {
      toastr.error('Error', 'Sólo se permiten archivos con extensión .xlsx');
      e.target.value = null;
      setSelectedFile(null);
    }
  };

  const validateDownloadTemplate = () => {
    const formattedStartDate = normalizeDate(startDate);
    const formattedEndDate = normalizeDate(endDate);

    if (!formattedStartDate || !formattedEndDate) {
      toastr.error(
        'Error',
        'Debe seleccionar la fecha desde y la fecha hasta para descargar la plantilla',
      );
      return null;
    }

    if (formattedStartDate > formattedEndDate) {
      toastr.error(
        'Error',
        'La fecha desde no puede ser mayor que la fecha hasta',
      );
      return null;
    }

    return {
      corporate: !!corporate,
      startDate: formattedStartDate,
      endDate: formattedEndDate,
    };
  };

  const handleDownloadTemplate = () => {
    const payload = validateDownloadTemplate();

    if (!payload) return;

    dispatchGetPricingProfileMassiveUpdateTemplate(payload);
  };

  const submitForm = (formValues) => {
    if (!selectedFile) {
      toastr.error('Error', 'Debe seleccionar un archivo .xlsx');
      return;
    }

    if (!isValidFileType(selectedFile)) {
      toastr.error(
        'Error',
        'El archivo seleccionado no es un archivo .xlsx válido',
      );
      return;
    }

    const formattedStartDate = normalizeDate(formValues.startDate);
    const formattedEndDate = normalizeDate(formValues.endDate);

    if (!formattedStartDate || !formattedEndDate) {
      toastr.error('Error', 'Debe seleccionar la fecha desde y la fecha hasta');
      return;
    }

    if (formattedStartDate > formattedEndDate) {
      toastr.error(
        'Error',
        'La fecha desde no puede ser mayor que la fecha hasta',
      );
      return;
    }

    const newFormValues = {
      ...formValues,
      corporate: !!formValues.corporate,
      startDate: formattedStartDate,
      endDate: formattedEndDate,
      file: selectedFile,
    };

    onSubmit(newFormValues);
  };

  if (loading) return <Loader />;

  return (
    <Form
      onSubmit={handleSubmit(submitForm)}
      style={{
        paddingTop: '20px',
      }}
    >
      <FormGroup style={{ marginBottom: '35px' }}>
        <Row className="align-items-center">
          <Col md={3} sm={12}>
            <Label>RANGO DE FECHAS</Label>
          </Col>

          <Col md={4} sm={5} xs={12}>
            <Field
              name="startDate"
              component={DatePicker}
              placeholder="Fecha Desde"
              validate={[isRequired]}
            />
          </Col>

          <Col
            md={1}
            sm={2}
            xs={12}
            style={{
              textAlign: 'center',
              fontWeight: 'bold',
              fontSize: '18px',
              paddingLeft: '0',
              paddingRight: '0',
            }}
          >
            -
          </Col>

          <Col md={4} sm={5} xs={12}>
            <Field
              name="endDate"
              component={DatePicker}
              placeholder="Fecha Hasta"
              validate={[isRequired]}
            />
          </Col>
        </Row>
      </FormGroup>

      <FormGroup style={{ marginBottom: '35px' }}>
        <Row className="align-items-center">
          <Col md={{ size: 4, offset: 2 }} sm={12}>
            <Button
              type="button"
              onClick={handleDownloadTemplate}
              style={{
                minWidth: '180px',
              }}
            >
              DESCARGAR PLANTILLA
            </Button>
          </Col>
        </Row>
      </FormGroup>

      <FormGroup style={{ marginBottom: '35px' }}>
        <Row className="align-items-center">
          <Col md={3} sm={12}>
            <Label
              style={{
                marginBottom: 0,
                fontSize: '14px',
                fontWeight: 500,
              }}
            >
              Adjuntar Excel:
            </Label>
          </Col>

          <Col md={5} sm={12}>
            <Input
              name="voucher"
              type="file"
              accept=".xlsx"
              onChange={onChangeVoucher}
            />
            {selectedFile && (
              <div style={{ marginTop: '10px' }}>
                <FileDetailResource file={selectedFile} />
              </div>
            )}
          </Col>
        </Row>
      </FormGroup>

      <FormGroup row>
        <FormItem label="Descripción">
          <Field
            name="description"
            component={TextInput}
            placeholder="Descripción"
            type="textarea"
          />
        </FormItem>
      </FormGroup>
      <FormFooter />
    </Form>
  );
};

PricingProfileMassiveUpdateForm.propTypes = {
  handleSubmit: PropTypes.func.isRequired,
  loading: PropTypes.bool.isRequired,
  onSubmit: PropTypes.func.isRequired,
  dispatchGetPricingProfileMassiveUpdateTemplate: PropTypes.func.isRequired,
  corporate: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  startDate: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  endDate: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
};

PricingProfileMassiveUpdateForm.defaultProps = {
  corporate: false,
  startDate: null,
  endDate: null,
};

const selector = formValueSelector('PricingProfileMassiveUpdateForm');

const mapStateToProps = (state) => ({
  loading: !state.SalesUnit.PricingProfileMassiveUpdate.getIn([
    'current',
    'activity',
  ]).isEmpty(),
  startDate: selector(state, 'startDate'),
  endDate: selector(state, 'endDate'),
  corporate: selector(state, 'corporate'),
});

const mapDispatchToProps = {
  dispatchGetPricingProfileMassiveUpdateTemplate:
    getPricingProfileMassiveUpdateTemplate,
};

const formComponent = reduxForm({
  form: 'PricingProfileMassiveUpdateForm',
})(PricingProfileMassiveUpdateForm);

export default connect(mapStateToProps, mapDispatchToProps)(formComponent);
